// Configuration Constants
const ETX_CONFIG = {
  VIEWPORT_OFFSET: -10,
  ANIMATION_REPEAT: true,
  ANIMATION_STAGGER: 150,
  COUNT_DURATION: 1750,
};

// Element Management
class AnimationElementManager {
  constructor() {
    this.elements = {
      willAppear: new Set(),
      didAppear: new Set(),
      willDrawPath: new Set(),
      didDrawPath: new Set(),
      willCountUp: new Set(),
      didCountUp: new Set(),
    };
    this.timeouts = new Set();
    this.isInitialLoad = !window.etxInitialLoadDone;
    window.etxInitialLoadDone = true;
  }

  addElement(className, element) {
    this.elements[className]?.add(element);
  }

  removeElement(className, element) {
    this.elements[className]?.delete(element);
  }

  moveElement(fromClass, toClass, element) {
    this.removeElement(fromClass, element);
    this.addElement(toClass, element);
  }

  clearTimeouts() {
    this.timeouts.forEach(clearTimeout);
    this.timeouts.clear();
  }

  addTimeout(timeoutId) {
    this.timeouts.add(timeoutId);
  }

  getElements(className) {
    return Array.from(this.elements[className] || []);
  }
}

// Utility Functions
const getOffsetTop = (el) => {
  let top = 0;
  while (el) {
    top += el.offsetTop;
    el = el.offsetParent;
  }
  return top;
};

const isAboveViewport = (el) => {
  return getOffsetTop(el) < window.scrollY;
};

const isBelowViewport = (el, offset = ETX_CONFIG.VIEWPORT_OFFSET) => {
  const topElement = getOffsetTop(el);
  const bottomScreen = window.scrollY + window.innerHeight - offset;
  return topElement > bottomScreen;
};

const inViewport = (el, offset = ETX_CONFIG.VIEWPORT_OFFSET) => {
  const topElement = getOffsetTop(el);
  const bottomElement = topElement + el.offsetHeight;
  const bottomScreen = window.scrollY + window.innerHeight - offset;
  const topScreen = window.scrollY;
  return bottomScreen > topElement && topScreen < bottomElement;
};

// SVG Path Helper Functions
const calculateRenderedPathLength = (path) => {
  const svg = path.ownerSVGElement;
  if (!svg) return null;

  const userLen = path.getTotalLength();
  const vb = svg.viewBox?.baseVal;

  if (!vb || !vb.width || !vb.height) return userLen;

  const rect = svg.getBoundingClientRect();
  if (!rect.width || !rect.height) return userLen;

  const scaleX = rect.width / vb.width;
  const scaleY = rect.height / vb.height;
  const scale = (scaleX + scaleY) / 2;

  return userLen * scale;
};

const setPathsHidden = (wrapper) => {
  wrapper.querySelectorAll("svg path").forEach((path) => {
    const userLen = path.getTotalLength();

    // Cache user length
    if (!path.dataset.pathLengthUser) {
      path.dataset.pathLengthUser = String(userLen);
    }

    const renderedLen = calculateRenderedPathLength(path) ?? userLen;

    // Set to hidden state
    path.style.animation = "none";
    path.style.strokeDasharray = String(renderedLen);
    path.style.strokeDashoffset = String(renderedLen);
    path.style.setProperty("--stripe-start", String(renderedLen));
    path.classList.remove("is-drawn");
  });
};

const drawPaths = (wrapper) => {
  wrapper.querySelectorAll("svg path").forEach((path) => {
    if (!path.dataset.pathLengthUser) {
      path.dataset.pathLengthUser = String(path.getTotalLength());
    }

    const userLen = Number(path.dataset.pathLengthUser);
    const renderedLen = calculateRenderedPathLength(path) ?? userLen;

    // Set dash values
    path.style.strokeDasharray = String(renderedLen);
    path.style.strokeDashoffset = String(renderedLen);
    path.style.setProperty("--stripe-start", String(renderedLen));

    // Force reflow and restart animation
    path.getBoundingClientRect();
    path.style.animation = "none";
    path.getBoundingClientRect();

    path.style.animation = "headline-stripe var(--transition-duration) ease-out forwards";
    path.classList.add("is-drawn");
  });
};

const animateCountUp = (el, countTo) => {
  const start = parseInt(el.textContent, 10) || 0;
  const startTime = performance.now();

  const animate = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / ETX_CONFIG.COUNT_DURATION, 1);
    const currentCount = Math.floor(start + (countTo - start) * progress);
    el.textContent = currentCount;

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      el.textContent = countTo;
    }
  };

  requestAnimationFrame(animate);
};

// Main Viewport Handler
function etxInViewport() {
  console.log("etxInViewport initialised");

  const manager = window.etxAnimationManager || new AnimationElementManager();
  window.etxAnimationManager = manager;

  const isShopifyDesignMode = document.documentElement.classList.contains("shopify-design-mode");
  const scrollingUp = window.etxLastScrollY !== undefined && window.scrollY < window.etxLastScrollY;

  // Collect all elements on first run
  if (manager.isInitialLoad) {
    document.querySelectorAll(".will-appear").forEach(el => manager.addElement('willAppear', el));
    document.querySelectorAll(".will-drawPath").forEach(el => manager.addElement('willDrawPath', el));
    document.querySelectorAll(".will-countUp").forEach(el => manager.addElement('willCountUp', el));
    manager.isInitialLoad = false;
  }

  // ---------------------------------------------------------------------------
  // COUNT UP ANIMATION
  // ---------------------------------------------------------------------------
  manager.getElements('willCountUp').forEach((el) => {
    if (!el.isConnected) {
      manager.removeElement('willCountUp', el);
      return;
    }

    const countTo = parseInt(el.getAttribute("data-count"), 10);
    if (Number.isNaN(countTo)) return;

    // Initial load: instantly show count for elements above viewport
    if (window.etxInitialLoadDone === true && isAboveViewport(el)) {
      el.textContent = countTo;
      el.classList.remove("will-countUp");
      el.classList.add("did-countUp");
      manager.moveElement('willCountUp', 'didCountUp', el);
      return;
    }

    // Element is in viewport: trigger animation
    if (inViewport(el)) {
      el.classList.remove("will-countUp");
      el.classList.add("did-countUp");
      manager.moveElement('willCountUp', 'didCountUp', el);
      animateCountUp(el, countTo);
    }
  });
  
  if (!isShopifyDesignMode) {
    let staggerDelay = 0;

    // ---------------------------------------------------------------------------
    // WILL APPEAR ANIMATION
    // ---------------------------------------------------------------------------
    manager.getElements('willAppear').forEach((el) => {
      if (!el.isConnected) {
        manager.removeElement('willAppear', el);
        return;
      }

      // Initial load: instantly show elements above viewport
      if (window.etxInitialLoadDone === true && isAboveViewport(el)) {
        el.classList.remove("will-appear");
        el.classList.add("did-appear");
        manager.moveElement('willAppear', 'didAppear', el);
        return;
      }

      // Element is in viewport: trigger animation
      if (inViewport(el)) {
        staggerDelay += ETX_CONFIG.ANIMATION_STAGGER;

        const timeoutId = setTimeout(() => {
          if (el.isConnected) {
            el.classList.remove("will-appear");
            el.classList.add("did-appear");
            manager.moveElement('willAppear', 'didAppear', el);
          }
          manager.timeouts.delete(timeoutId);
        }, staggerDelay);

        manager.addTimeout(timeoutId);
      }
    });

    // Reset when scrolling up
    if (ETX_CONFIG.ANIMATION_REPEAT && scrollingUp) {
      manager.getElements('didAppear').forEach((el) => {
        if (!el.isConnected) {
          manager.removeElement('didAppear', el);
          return;
        }

        if (!inViewport(el) && isBelowViewport(el)) {
          el.classList.remove("did-appear");
          el.classList.add("will-appear");
          manager.moveElement('didAppear', 'willAppear', el);
        }
      });
    }

    // ---------------------------------------------------------------------------
    // DRAW SVG PATH ANIMATION
    // ---------------------------------------------------------------------------
    staggerDelay = 0;

    manager.getElements('willDrawPath').forEach((wrapper) => {
      if (!wrapper.isConnected) {
        manager.removeElement('willDrawPath', wrapper);
        return;
      }

      // Initial load: instantly draw elements above viewport
      if (window.etxInitialLoadDone === true && isAboveViewport(wrapper)) {
        wrapper.querySelectorAll("svg path").forEach((path) => {
          path.style.strokeDashoffset = "0";
          path.classList.add("is-drawn");
        });
        wrapper.classList.remove("will-drawPath");
        wrapper.classList.add("did-drawPath");
        manager.moveElement('willDrawPath', 'didDrawPath', wrapper);
        return;
      }

      // Set hidden state for elements not yet animated
      if (!wrapper.dataset.pathsInitialized) {
        setPathsHidden(wrapper);
        wrapper.dataset.pathsInitialized = "true";
      }

      // Element is in viewport: trigger animation
      if (inViewport(wrapper)) {
        staggerDelay += ETX_CONFIG.ANIMATION_STAGGER;

        const timeoutId = setTimeout(() => {
          if (wrapper.isConnected && wrapper.querySelectorAll("svg path").length) {
            drawPaths(wrapper);
            wrapper.classList.remove("will-drawPath");
            wrapper.classList.add("did-drawPath");
            manager.moveElement('willDrawPath', 'didDrawPath', wrapper);
          }
          manager.timeouts.delete(timeoutId);
        }, staggerDelay);

        manager.addTimeout(timeoutId);
      }
    });

    // Reset when scrolling up
    if (ETX_CONFIG.ANIMATION_REPEAT && scrollingUp) {
      manager.getElements('didDrawPath').forEach((wrapper) => {
        if (!wrapper.isConnected) {
          manager.removeElement('didDrawPath', wrapper);
          return;
        }

        if (!inViewport(wrapper) && isBelowViewport(wrapper)) {
          wrapper.classList.remove("did-drawPath");
          wrapper.classList.add("will-drawPath");
          setPathsHidden(wrapper);
          manager.moveElement('didDrawPath', 'willDrawPath', wrapper);
        }
      });
    }


    // Reset when scrolling up
    if (ETX_CONFIG.ANIMATION_REPEAT && scrollingUp) {
      manager.getElements('didCountUp').forEach((el) => {
        if (!el.isConnected) {
          manager.removeElement('didCountUp', el);
          return;
        }

        if (!inViewport(el) && isBelowViewport(el)) {
          el.textContent = "0";
          el.classList.remove("did-countUp");
          el.classList.add("will-countUp");
          manager.moveElement('didCountUp', 'willCountUp', el);
        }
      });
    }
  }

  // Update last scroll position
  window.etxLastScrollY = window.scrollY;
}

// Recalculate Path Lengths on Resize
function etxRecalcPathLengths() {
  const manager = window.etxAnimationManager;
  if (!manager) return;

  manager.getElements('didDrawPath').forEach((wrapper) => {
    if (!wrapper.isConnected) return;

    wrapper.querySelectorAll("svg path").forEach((path) => {
      if (!path.dataset.pathLengthUser) return;

      const userLen = Number(path.dataset.pathLengthUser);
      const renderedLen = calculateRenderedPathLength(path) ?? userLen;

      path.style.strokeDasharray = String(renderedLen);
      path.style.strokeDashoffset = path.classList.contains("is-drawn") ? "0" : String(renderedLen);
      path.style.setProperty("--stripe-start", String(renderedLen));
    });
  });
}

// RequestAnimationFrame Throttle
const createRafThrottle = (callback) => {
  let rafId = null;
  let lastArgs = null;

  return (...args) => {
    lastArgs = args;
    
    if (rafId !== null) return;

    rafId = requestAnimationFrame(() => {
      callback(...lastArgs);
      rafId = null;
      lastArgs = null;
    });
  };
};

// Event Handlers
const handleScroll = createRafThrottle(etxInViewport);
const handleResize = createRafThrottle(() => {
  etxRecalcPathLengths();
  etxInViewport();
});

// Initialize Event Listeners
if (typeof window !== 'undefined') {
  window.addEventListener("scroll", handleScroll, { passive: true });
  window.addEventListener("load", etxInViewport);
  window.addEventListener("resize", handleResize, { passive: true });
}

// Cleanup function (optional, for SPA scenarios)
function etxCleanup() {
  const manager = window.etxAnimationManager;
  if (manager) {
    manager.clearTimeouts();
  }
  
  window.removeEventListener("scroll", handleScroll);
  window.removeEventListener("resize", handleResize);
}

// Export for external use if needed
window.etxInViewport = etxInViewport;
window.etxRecalcPathLengths = etxRecalcPathLengths;
window.etxCleanup = etxCleanup;